import { inject, Injectable } from "@angular/core";
import { MaterialApi } from "../Api/MaterialApi";
import { MaterialState } from "./MaterialState";
import { SearchMaterialRequest } from "../Dto/MaterialSearchRequest";
import { CreateMaterialRequest } from "../Dto/MaterialCreateRequest";
import { UpdateMaterialRequest } from "../Dto/MaterialUpdateRequest";
import { MaterialListItem } from "../Dto/MaterialModel";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class MaterialEffects {
  private readonly materialApi = inject(MaterialApi);
  private readonly materialState = inject(MaterialState);

  async searchMaterials(params: SearchMaterialRequest = {}): Promise<void> {
    const mergedParams: SearchMaterialRequest = {
      ...this.materialState.searchParams(),
      ...params,
    };

    this.materialState.setLoading(true);
    this.materialState.setError(null);

    try {
      const response = await this.materialApi.searchMaterials(mergedParams);
      this.materialState.setMaterials(response.data, response.pagination);
      this.materialState.setSearchParams(mergedParams);
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.materialState.setError(errorMessage);
    } finally {
      this.materialState.setLoading(false);
    }
  }

  async createMaterial(payload: CreateMaterialRequest): Promise<MaterialListItem> {
    this.materialState.setLoading(true);
    this.materialState.setError(null);

    try {
      const material = await this.materialApi.createMaterial(payload);
      this.materialState.addMaterialToState(material);
      return material;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.materialState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.materialState.setLoading(false);
    }
  }

  async updateMaterial(materialId: string, payload: UpdateMaterialRequest): Promise<MaterialListItem> {
    this.materialState.setLoading(true);
    this.materialState.setError(null);

    try {
      const material = await this.materialApi.updateMaterial(materialId, payload);
      this.materialState.updateMaterialInState(materialId, material);
      return material;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.materialState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.materialState.setLoading(false);
    }
  }

  async deleteMaterial(materialId: string): Promise<boolean> {
    this.materialState.setLoading(true);
    this.materialState.setError(null);

    try {
      await this.materialApi.deleteMaterial(materialId);
      this.materialState.removeMaterialFromState(materialId);
      return true;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.materialState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.materialState.setLoading(false);
    }
  }

  async updateMaterialImage(materialId: string, imageUrl: string): Promise<MaterialListItem> {
    this.materialState.setLoading(true);
    this.materialState.setError(null);

    try {
      const material = await this.materialApi.updateMaterialImage(materialId, imageUrl);
      this.materialState.updateMaterialInState(materialId, material);
      return material;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.materialState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.materialState.setLoading(false);
    }
  }

  async deleteMaterialImage(materialId: string): Promise<boolean> {
    this.materialState.setLoading(true);
    this.materialState.setError(null);

    try {
      await this.materialApi.deleteMaterialImage(materialId);
      this.materialState.updateMaterialInState(materialId, { imageUrl: null });
      return true;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.materialState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.materialState.setLoading(false);
    }
  }
}
